<template>
  <div class="my-3">
    <div class="p-3 rounded bg-secondary-3 service-container">
      <div class="row">
        <div class="col-12 col-md-4">
          <div class="color-secondary-6 font-14">
            Equipo:
          </div>
          <div class="font-weight-bold">
            {{ item.equipment?.name | empty }}
          </div>
        </div>
        <div class="col-12 col-md-3">
          <div class="color-secondary-6 font-14">
            Tipo de Servicio:
          </div>
          <div class="font-weight-bold">
            {{ item.serviceType?.name | empty }}
          </div>
        </div>
        <div class="col-12 col-md">
          <div class="color-secondary-6 font-14">
            Estatus:
          </div>
          <div class="font-weight-bold">
            <custom-balloon
              :background-color="item.status.color"
              :text="item.status.name"
              dot
            />
          </div>
        </div>
        <div class="col-12 col-md" v-if="showActionItems">
          <drop-down
            :items="actionItems"
            always-show-dropdown
            class="float-md-right mr-5"
          />
        </div>
      </div>
      <div class="col-12 border-bottom mt-3"></div>
      <div class="row">
        <div class="col-12 col-md-4">
          <div class="my-3 pt-2">
            <div class="color-secondary-6 font-14">
              Procede:
            </div>
            <b-form-group v-slot="{ ariaDescribedby }">
              <validation-provider v-slot="{ errors }" rules="required">
                <b-form-radio-group
                  v-model="item.isProceed"
                  :disabled="isReadOnly"
                  :id="`radio-group-isProceed-${item.key}`"
                  :name="`radio-options-isProceed-${item.key}`"
                  :options="yesNoOptions"
                  :aria-describedby="ariaDescribedby"
                  @input="onProceedChange"
                ></b-form-radio-group>
                <error
                  v-if="errors[0]"
                  variant="no-border"
                  message="El campo es requerido."
                />
              </validation-provider>
            </b-form-group>
          </div>
        </div>
        <div class="col-12 col-md-3">
          <div class="my-3">
            <div class="color-secondary-6 font-14">
              Clasificación de Servicio:
            </div>
            <validation-provider
              v-slot="{ errors }"
              :rules="!isClasificationDisabled ? 'required' : ''"
            >
              <custom-drop-down
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                v-model="item.classification"
                :options="classifications"
                :disabled="isClasificationDisabled || isReadOnly"
                :error="errors[0]"
                custom-error-msg="La Clasificación de Servicio es requerida"
              />
            </validation-provider>
          </div>
        </div>
      </div>
      <div class="row" v-if="showPurchaseInputs">
        <div class="col-12 col-md-4">
          <div class="pt-2">
            <div class="color-secondary-6 font-14">
              Requiere Compra:
            </div>
            <b-form-group v-slot="{ ariaDescribedby }">
              <validation-provider
                v-slot="{ errors }"
                :rules="item.isProceed ? 'required' : ''"
              >
                <b-form-radio-group
                  v-model="item.isRequiredReplacement"
                  :id="`radio-group-purchase-required-${item.key}`"
                  :name="`radio-options-purchase-required-${item.key}`"
                  :options="yesNoOptions"
                  :disabled="isReadOnly || !item.isProceed"
                  :aria-describedby="ariaDescribedby"
                  @input="onRequiredReplacementChange"
                ></b-form-radio-group>
                <error
                  v-if="errors[0]"
                  variant="no-border"
                  message="El campo es requerido."
                />
              </validation-provider>
            </b-form-group>
          </div>
        </div>
        <div class="col-12 col-md-3">
          <div>
            <div class="color-secondary-6 font-14">
              Método de Compra:
            </div>
            <validation-provider
              v-slot="{ errors }"
              :rules="!isPurchaseMethodDisabled ? 'required' : ''"
            >
              <custom-drop-down
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                v-model="item.purchaseMethod"
                :options="purchaseMethods"
                :disabled="isPurchaseMethodDisabled || isReadOnly"
                :error="errors[0]"
                custom-error-msg="El Método de Compra es requerido"
              />
            </validation-provider>
          </div>
        </div>
        <div v-if="isInventoryPurchaseMethod" class="col-12 col-md">
          <div class="color-secondary-6 font-14">
            Tipo del Inventario:
          </div>
          <div>
            <b-form-group v-slot="{ ariaDescribedby }">
              <validation-provider v-slot="{ errors }" rules="required">
                <b-form-radio-group
                  v-model="item.inventoryType"
                  :id="`radio-group-inventory-type-${item.key}`"
                  :name="`radio-group-inventory-type-${item.key}`"
                  :disabled="isReadOnly"
                  :options="inventoryTypes"
                  :aria-describedby="ariaDescribedby"
                  @input="onInventoryChange"
                ></b-form-radio-group>
                <error
                  v-if="errors[0]"
                  variant="no-border"
                  message="El tipo del inventario es requerido."
                />
              </validation-provider>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>
    <service-documentation
      v-if="item.record"
      :methods="documentationMethods"
      :data="item.documentation"
      @cancel="onCancelDocumentation"
      @save="onSaveDocumentation"
    ></service-documentation>
  </div>
</template>

<script>
import Error from 'custom-error';
import CustomDropDown from 'custom-drop-down';
import CustomBalloon from 'custom-balloon';
import DropDown from 'dropdown';
import ServiceDocumentation from './ServiceDocumentation.vue';

import PurchaseMethods from '@/utils/constants/purchaseMethods';
import PeiAtReferralTypes from '@/utils/constants/peiAtReferralType';
import ClassificationServiceStatuses from '@/utils/constants/peiAtClassificationServiceStatuses';

export default {
  name: 'ReferralServiceItem',
  components: {
    Error,
    CustomDropDown,
    CustomBalloon,
    DropDown,
    ServiceDocumentation,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    inventoryTypes: {
      type: Array,
      required: true,
    },
    classifications: {
      type: Array,
      required: true,
    },
    purchaseMethods: {
      type: Array,
      required: true,
    },
    documentationMethods: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    yesNoOptions() {
      return [
        {
          value: true,
          text: 'Si',
        },
        {
          value: false,
          text: 'No',
        },
      ];
    },
    showPurchaseInputs() {
      return (
        this.item.referral.referralTypeId ==
        PeiAtReferralTypes.EquipmentRelatedServiceRequest
      );
    },
    isClasificationDisabled() {
      return this.isReadOnly || !this.item.isProceed;
    },
    isPurchaseMethodDisabled() {
      return this.isClasificationDisabled || !this.item.isRequiredReplacement;
    },
    isInventoryPurchaseMethod() {
      return this.item.purchaseMethod?.id === PurchaseMethods.Inventory;
    },
    showActionItems() {
      return this.actionItems.some((x) =>
        typeof x.visible === 'function' ? x.visible() : x.visible
      );
    },
    actionItems() {
      return [
        {
          name: 'Realizado',
          icon: 'fas fa-calendar-edit text-success',
          click: () => this.onPerformService(),
          visible: () => {
            const isEquipmentRelated =
              this.item.referral.referralTypeId ===
              PeiAtReferralTypes.EquipmentRelatedServiceRequest;

            const isTechnicalAssistance =
              this.item.referral.referralTypeId ===
              PeiAtReferralTypes.TechnicalAssistanceServiceRequest;

            return (
              ((isTechnicalAssistance &&
                Boolean(this.item.status.id) &&
                this.item.status.id !==
                  ClassificationServiceStatuses.ConServicio) ||
                (isEquipmentRelated &&
                  this.item.isProceed &&
                  this.item.isRequiredReplacement === false)) &&
              this.item.status.id ==
                ClassificationServiceStatuses.ConServicioPendiente
            );
          },
        },
        {
          name: 'Ver Documentación',
          icon: 'fas fa-file-alt text-primary',
          visible: () => Boolean(this.item.documentation),
          click: () => this.onPerformService(),
        },
      ];
    },
  },
  methods: {
    onProceedChange(value) {
      this.$emit('proceed', { value, item: this.item });
    },
    onRequiredReplacementChange(value) {
      this.$emit('required-replacement', { value, item: this.item });
    },
    onInventoryChange(value) {
      this.$emit('inventory-type', { value, item: this.item });
    },
    onCancelDocumentation() {
      this.$emit('cancel-documentation', this.item);
    },
    onSaveDocumentation(model) {
      this.$emit('save-documentation', { model, item: this.item });
    },
    onPerformService() {
      this.$emit('perform-service', this.item);
    },
  },
};
</script>

<style scoped lang="scss">
.service-container {
  border-left: 5px solid #d9e5df;
}

::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
</style>
